import DOMPurify from 'dompurify';
import { FaRegCircle } from 'react-icons/fa6';
import React, { useRef, useState } from 'react';
import { BsExclamationDiamond } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const ProcessInfo = ({ procedure }) => {
  const navigate = useNavigate();

  const contentRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const styleImages = htmlContent => {
    const doc = new DOMParser()?.parseFromString(htmlContent, 'text/html');
    const images = doc?.querySelectorAll('img');

    images.forEach(img => {
      img.style.width = '80px';
      img.style.height = 'auto';
    });

    return doc.body.innerHTML;
  };

  const getTitleByIdOrTitle = (idOrTitle, procedure) => {
    if (!procedure?.procedures) return null;

    for (const item of procedure.procedures) {
      if (
        item.steps &&
        (item._id === idOrTitle || item.steps.title === idOrTitle)
      ) {
        return item.steps.title;
      }
      if (
        item.decisions &&
        (item._id === idOrTitle || item.decisions.title === idOrTitle)
      ) {
        return item.decisions.title;
      }
    }
    return 'Title not found';
  };

  const handleFlowchart = () => {
    const id = window.location.pathname.split('/').pop();
    navigate(`/procedure-flowchart/${id}`);
  };

  const numberOfSteps = procedure?.procedures?.length || 0;
  const stepHeight = 60;
  const totalHeight = (numberOfSteps + 1) * stepHeight + 40;

  const handleStepClick = index => {
    setSelectedIndex(index);
  };

  const handleFlowchartClick = index => {
    setSelectedIndex(index);
  };

  const sanitizedSafetyNoteInfo = DOMPurify.sanitize(
    procedure?.procedureSafetyNote?.procedureNoteInfo || ''
  );
  const styledsafetyNoteInfo = styleImages(sanitizedSafetyNoteInfo);

  return (
    <div className="p-6 h-[60vh] overflow-y-auto ">
      <h2 className="text-1xl font-bold mb-4">Safety Notes</h2>

      <div
        id={procedure?.procedureSafetyNote?.title ?? 'Untitled Safety Note'}
        key={'1'}
        className={`cursor-pointer p-2 rounded-tl-md ${''}`}
      >
        <h3
          className={`text-2xl font-semibold mb-2 flex flex-row ${'text-blue-500'}`}
        >
          <span
            className={`h-[30px] w-[30px] rounded-[50%] flex justify-center font-10 items-center mr-2 ${'bg-black text-white'}`}
          >
            {'S'}
          </span>
          {procedure?.procedureSafetyNote?.title ?? 'Untitled Safety Note'}
        </h3>
        <p
          // className="text-gray-700 mb-4"
          dangerouslySetInnerHTML={{ __html: styledsafetyNoteInfo }}
        />
        <hr className="border-t-2 border-gray-300" />
      </div>
      <div className="flex flex-row items-center">
        {/* steps info-- */}

        <div className="w-2/3">
          <section id="process-steps">
            <h2 className="text-1xl font-bold mb-4">Steps and Decisions</h2>

            <div className="space-y-8">
              {[...(procedure?.procedures || [])].sort((a, b) => a.sequence - b.sequence).map((item, index) => {
                const sanitizedStepInfo = DOMPurify.sanitize(
                  item?.steps?.stepInfo || ''
                );
                const sanitizedDecisionInfo = DOMPurify.sanitize(
                  item?.decisions?.decisionInfo || ''
                );

                // styles to images
                const styledStepInfo = styleImages(sanitizedStepInfo);
                const styledDecisionInfo = styleImages(sanitizedDecisionInfo);

                const isSelected = index === selectedIndex;

                return item?.steps ? (
                  <div
                    key={index}
                    ref={el => (contentRef.current[index] = el)}
                    onClick={() => handleStepClick(index)}
                    className={`cursor-pointer p-2 rounded-tl-md ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : ''
                    }`}
                  >
                    <h3
                      id={item?.steps?.title || 'Untitled Step'}
                      className={`text-2xl font-semibold mb-2 flex flex-row ${
                        isSelected ? 'text-blue-500' : ''
                      }`}
                    >
                      <span
                        className={`h-[30px] w-[30px] rounded-[50%] flex justify-center items-center mr-2 ${
                          isSelected
                            ? 'bg-blue-500 text-white'
                            : 'bg-black text-white'
                        }`}
                      >
                        {index + 1}
                      </span>
                      {item?.steps?.title || 'Untitled Step'}
                    </h3>
                    <p
                      className="text-gray-700 mb-4"
                      dangerouslySetInnerHTML={{ __html: styledStepInfo }}
                    />
                    <hr className="border-t-2 border-gray-300" />
                  </div>
                ) : (
                  <div
                    key={index}
                    ref={el => (contentRef.current[index] = el)}
                    onClick={() => handleStepClick(index)}
                    className={`cursor-pointer p-2 rounded-tl-md ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : ''
                    }`}
                  >
                    <h3
                      className={`text-2xl font-semibold mb-2 flex flex-row ${
                        isSelected ? 'text-blue-500' : ''
                      }`}
                      id={item?.decisions?.title || 'Untitled Decision'}
                    >
                      <label className="flex mr-2">
                        <span className="pr-2"> {index + 1}</span>
                        <BsExclamationDiamond size={30} />
                      </label>
                      {item?.decisions?.title || 'Untitled Decision'}
                    </h3>
                    <p
                      className="text-gray-700 mb-4"
                      dangerouslySetInnerHTML={{ __html: styledDecisionInfo }}
                    />
                    <hr className="border-t-2 border-gray-300" />

                    <h3 className="text-md font-semibold mb-2 mt-2">
                      <label>ANSWERS</label>
                      <div className="h-[10vh] overflow-y-auto">
                        {item?.decisions?.answer?.map((answer, idx) => (
                          <a
                            href={`#${answer._associate == 'endStep' ? procedure?.endProcedure?.title || 'endStep' : getTitleByIdOrTitle(answer._associate, procedure)}`}
                            className="z-10"
                          >
                            {' '}
                            <div
                              className="w-full mt-2 flex justify-between items-center border border-b-slate-300 p-2"
                              key={idx}
                            >
                              <div className="flex flex-row items-center justify-center">
                                <FaRegCircle
                                  style={{
                                    color: 'grey',
                                    marginTop: '3px',
                                    marginRight: '3px',
                                  }}
                                  size={10}
                                />
                                <h2 className="ml-2">{answer?._ans}</h2>
                              </div>

                              <div>
                                {answer?.type === 'steps' ? (
                                  <span
                                    className={`h-[${answer._associate != 'endStep' ? '20px' : '30px'}] w-[30px] ${answer._associate != 'endStep' ? 'bg-black' : 'bg-red-500'} rounded-full text-white flex justify-center items-center`}
                                  >
                                    {answer._associate != 'endStep'
                                      ? procedure.procedures.findIndex(i => {
                                          return (
                                            i._id === answer._associate ||
                                            i.steps?.title === answer._associate
                                          );
                                        }) + 1
                                      : 'ES'}
                                  </span>
                                ) : (
                                  <label className="flex items-center space-x-1">
                                    <span>
                                      {procedure.procedures.findIndex(i => {
                                        return (
                                          i._id === answer._associate ||
                                          i.decisions?.title ===
                                            answer._associate
                                        );
                                      }) + 1}
                                    </span>
                                    <BsExclamationDiamond size={20} />
                                  </label>
                                )}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </h3>
                  </div>
                );
              })}
              <div
                key={numberOfSteps}
                ref={el => (contentRef.current[numberOfSteps] = el)}
                onClick={() => handleStepClick(numberOfSteps)}
                className={`cursor-pointer p-2 rounded-tl-md ${
                  selectedIndex === numberOfSteps
                    ? 'bg-gray-200 border border-gray-300'
                    : ''
                }`}
                id={procedure?.endProcedure?.title || 'endStep'}
              >
                <div
                  id={procedure?.endProcedure?.title || 'Untitled End Step'}
                  className={` font-semibold mb-2 flex flex-row ${
                    selectedIndex === numberOfSteps ? 'text-blue-500' : ''
                  }`}
                >
                  <span
                    className={`h-[30px] w-[30px] rounded-[50%] flex justify-center items-center mr-2 bg-red-500 text-white
                        }`}
                  >
                    ES
                  </span>
                  <h1 className="font-2xl">
                    {procedure?.endProcedure?.title || 'Untitled End Step'}
                  </h1>
                </div>
                <p
                  className="text-gray-700 mb-4"
                  dangerouslySetInnerHTML={{
                    __html: styleImages(
                      DOMPurify.sanitize(
                        procedure?.endProcedure?.stepInfo || ''
                      )
                    ),
                  }}
                />
                <hr className="border-t-2 border-gray-300" />
              </div>
            </div>
          </section>
        </div>

        {/* // flow chart */}

        <div
          onClick={handleFlowchart}
          className="w-1/3 flex flex-col content-start"
        >
          <div className="relative flex flex-col items-center">
            <div className="relative mb-8">
              <div className="relative flex flex-col items-center z-10">
                {numberOfSteps ? (
                  <div
                    className={`bg-blue-500 text-white rounded-full w-14 h-14 flex items-center justify-center mb-4 z-10 ${
                      selectedIndex === 0 ? 'border-4 border-yellow-400' : ''
                    }`}
                    onClick={() => handleFlowchartClick(0)}
                  >
                    Start
                  </div>
                ) : null}

                {[...(procedure?.procedures || [])].sort((a, b) => a.sequence - b.sequence).map((item, index) => (
                  <a
                    className="text-sm mt-1"
                    href={`#${item?.decisions?.title || item?.steps?.title}`}
                  >
                    {' '}
                    <div
                      key={`step-${index}`}
                      className={`rounded-lg w-48 text-center h-14 flex flex-col items-center justify-center mb-4 z-10 cursor-pointer
              ${
                item?.steps?.title
                  ? 'bg-green-100 border border-green-500 text-green-700' // Style for "Step"
                  : 'bg-yellow-100 border border-yellow-500 text-yellow-700' // Style for "Decision"
              }
              ${selectedIndex === index ? 'ring-2 ring-blue-700' : ''}
            `}
                      onClick={() => handleFlowchartClick(index)}
                    >
                      <span className="text-sm font-semibold">
                        {item?.steps?.title ? 'Step' : 'Decision'}
                      </span>
                      <span className="text-sm mt-1">
                        {item?.steps?.title?.length > 20
                          ? item.steps.title.substring(0, 20) + '...'
                          : item?.steps?.title ||
                            (item?.decisions?.title?.length > 20
                              ? item.decisions.title.substring(0, 20) + '...'
                              : item?.decisions?.title) ||
                            'Untitled'}
                      </span>
                    </div>
                  </a>
                ))}

                {numberOfSteps ? (
                  <a
                    className="text-sm mt-1"
                    href={`#${procedure?.endProcedure?.title}`}
                  >
                    {' '}
                    <div
                      key={`step-${numberOfSteps}`}
                      className={`bg-red-100 border border-red-500 rounded-lg w-40 h-14 flex text-center flex-col items-center justify-center mb-4 z-10 cursor-pointer ${
                        selectedIndex === numberOfSteps
                          ? 'ring-2 ring-blue-700'
                          : ''
                      }`}
                      onClick={() => handleFlowchartClick(numberOfSteps)}
                    >
                      <span className="text-sm font-semibold">End Step</span>
                      <span className="text-sm mt-1">
                        {procedure?.endProcedure?.title || 'Untitled End Step'}
                      </span>
                    </div>
                  </a>
                ) : null}
              </div>

              <div className="absolute inset-0 flex flex-col items-center z-0">
                <div
                  className="border-l-2 border-blue-500 w-0 absolute"
                  style={{
                    top: 0,
                    height: procedure?.endProcedure?.title
                      ? (procedure?.procedures?.length + 1) * stepHeight +
                        procedure?.procedures?.length * 18
                      : procedure?.procedures?.length * stepHeight +
                        procedure?.procedures?.length * 18, // Total height for all steps and spacing
                    left: '50%',
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessInfo;
